<template>
  <BaseSidebar
    v-model="modelValue"
    title="Pedido de excepcionalidade"
    description="Revise o resumo do pedido de excepcionalidade e, se necessário, cancele ou reverta a solicitação."
    :headerChildren="headerId"
  >
    <template v-if="stationContext" #contextHeader>
      <StationContextCard v-bind="stationContext" />
    </template>

    <div v-if="loading" class="flex justify-center items-center h-full min-h-[300px]">
      <v-progress-circular indeterminate color="primary" size="64" />
    </div>
    <div v-else-if="!detailedException" class="text-center py-8">
      <p class="text-textSecondary">Não foi possível carregar os detalhes do pedido.</p>
    </div>

    <div v-else-if="detailedException">
      <div v-if="!isConfirming" class="flex flex-col">
        <div class="mb-4 text-left">
          <span class="text-secondary font-bold text-lg">Resumo do pedido</span>
        </div>

        <!-- Reversão multi-etapa aguardando os demais validadores. -->
        <template v-if="hasPendingReversal">
          <div class="mb-4 text-left">
            <span
              class="inline-flex items-center rounded-full border border-warning bg-warning/20 px-3 py-1 text-xs font-bold text-yellow-800"
              data-testid="summary-reversal-pending-badge"
            >
              Reversão pendente
            </span>
          </div>
          <ExceptionalityProgressTimeline
            v-if="reversalTimelineProps"
            :header-status="reversalTimelineProps.headerStatus"
            :title="reversalTimelineProps.title"
            :layers="reversalTimelineProps.layers"
            :base-event="reversalTimelineProps.baseEvent"
            class="mb-4"
            data-testid="summary-reversal-progress-timeline"
          />
        </template>

        <template v-if="flags.enabledApprovalEngine && detailedException.approvalInstance">
          <ExceptionalityProgressTimeline
            v-if="timelineProps"
            :header-status="timelineProps.headerStatus"
            :title="timelineProps.title"
            :layers="timelineProps.layers"
            :base-event="timelineProps.baseEvent"
            class="mb-4"
            data-testid="summary-exceptionality-progress-timeline"
          />
          <div class="flex flex-col gap-y-2 pb-2">
            <StatusBadge :status="detailedException.status" />

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-2">
              <div class="flex flex-col text-left">
                <span class="text-secondary font-bold text-base">Data da solicitação</span>
                <span class="text-base text-secondary">{{
                  formatDateTimeWithTimezone(detailedException.requestDate)
                }}</span>
              </div>

              <div class="flex flex-col text-left">
                <span class="text-secondary font-bold text-base">Solicitante</span>
                <span class="text-base text-secondary">{{
                  detailedException.requester?.name || 'N/A'
                }}</span>
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-2">
              <div class="flex flex-col text-left">
                <span class="text-secondary font-bold text-base">Data da aprovação</span>
                <span class="text-base text-secondary">{{
                  detailedException.approvalDate
                    ? formatDateTimeWithTimezone(detailedException.approvalDate)
                    : 'Pendente'
                }}</span>
              </div>

              <div class="flex flex-col text-left">
                <span class="text-secondary font-bold text-base">Aprovadores</span>
                <span class="text-base text-secondary">{{
                  approvedApproversText || 'Nenhum até o momento'
                }}</span>
              </div>
            </div>
          </div>
        </template>

        <div v-else class="flex flex-col gap-y-2 pb-2">
          <div class="flex flex-col text-left">
            <span class="text-secondary font-bold text-base">Status</span>
            <StatusBadge :status="detailedException.status" />
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-2">
            <div class="flex flex-col text-left">
              <span class="text-secondary font-bold text-base">Data da solicitação</span>
              <span class="text-base text-secondary">{{
                formatDateToString(new Date(detailedException.requestDate))
              }}</span>
            </div>

            <div class="flex flex-col text-left">
              <span class="text-secondary font-bold text-base">Solicitante</span>
              <span class="text-base text-secondary">{{
                detailedException.requester?.name || 'N/A'
              }}</span>
            </div>

            <template v-if="evaluationDateDisplay">
              <div class="flex flex-col text-left">
                <span class="text-secondary font-bold text-base">{{ evaluationDateLabel }}</span>
                <span class="text-base text-secondary">{{ evaluationDateDisplay }}</span>
              </div>

              <div class="flex flex-col text-left">
                <span class="text-secondary font-bold text-base">Aprovador</span>
                <span class="text-base text-secondary">{{ evaluatorOrApproverName }}</span>
              </div>
            </template>
          </div>
        </div>

        <ExceptionSimpleCard
          class="mt-4"
          :title="`Modalidade de repasse`"
          :description="formatRepassMode(detailedException.changes.repassMode?.requested)"
        />

        <ExceptionSimpleCard
          class="mt-4"
          :title="`Regras de proporcionalidade`"
          :description="
            formatProportionalityRule(detailedException.changes.proportionalityRule?.requested)
          "
        />

        <ExceptionWorkingDayCard
          v-if="
            detailedException.changes.workJourney?.requested?.startHour &&
            detailedException.changes.workJourney?.requested?.endHour
          "
          class="mt-4"
          :start-time="new Date(detailedException.changes.workJourney.requested.startHour)"
          :end-time="new Date(detailedException.changes.workJourney.requested.endHour)"
          :lost-hours="detailedException.financials.lostHours"
          :estimated-loss="Number(detailedException.financials.estimatedLossTotal)"
        />

        <ExceptionAgreedValueCard
          class="mt-4"
          :original-hourly-value="Number(detailedException.changes.hourlyValue?.original || 0)"
          :exceptional-cost="Number(detailedException.changes.hourlyValue?.requested || 0)"
          :base-cost="Number(detailedException.financials.baseCost || 0)"
          :estimated-loss-total="Number(detailedException.financials.estimatedLossTotal || 0)"
          :base-duration="Number(detailedException.financials.baseDurationHours || 0)"
          :exceptional-duration="Number(detailedException.financials.exceptionalDurationHours || 0)"
        />

        <ExceptionSimpleCard
          class="mt-4"
          title="Justificativa da solicitação"
          :description="detailedException.justification || 'Nenhuma justificativa fornecida.'"
        />
      </div>

      <div v-else class="flex flex-col items-start gap-2">
        <h2 class="text-2xl font-bold text-textPrimary mb-4 text-left">
          {{ confirmationTitle }}
        </h2>
        <p class="text-secondary text-base text-left">
          {{ confirmationMessage }}
        </p>

        <div
          v-if="actionType === ExceptionActionType.REVERT"
          class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 pb-2 mt-2"
        >
          <div class="flex flex-col text-left">
            <span class="text-secondary font-bold text-base">Data da solicitação</span>
            <span class="text-base text-secondary">{{
              formatDateToString(new Date(detailedException.requestDate))
            }}</span>
          </div>

          <div class="flex flex-col text-left">
            <span class="text-secondary font-bold text-base">Solicitante</span>
            <span class="text-base text-secondary">{{
              detailedException.requester?.name || 'N/A'
            }}</span>
          </div>

          <div class="flex flex-col text-left" v-if="detailedException.approvalDate">
            <span class="text-secondary font-bold text-base">Data da aprovação</span>
            <span class="text-base text-secondary">{{
              formatDateToString(new Date(detailedException.approvalDate))
            }}</span>
          </div>

          <div class="flex flex-col text-left" v-if="detailedException.approver?.name">
            <span class="text-secondary font-bold text-base">Aprovador</span>
            <span class="text-base text-secondary">{{ detailedException.approver.name }}</span>
          </div>
        </div>

        <!-- Reversão multi-etapa: o clique já conta como voto no papel escolhido. -->
        <div
          v-if="actionType === ExceptionActionType.REVERT && isMultiStepReversal"
          class="w-full mt-4"
          data-testid="reversal-role-section"
        >
          <ApprovalRoleSelector
            v-model="selectedRole"
            :roles="availableReversalRoles"
            label="Solicitar como"
          />
          <p
            v-if="effectiveRole"
            class="text-sm text-textPrimary text-left"
            data-testid="reversal-vote-role-text"
          >
            Seu voto conta como {{ VALIDATION_ROLE_LABELS[effectiveRole] ?? effectiveRole }}. A
            reversão só vale após a validação dos demais responsáveis.
          </p>
        </div>

        <TextareaInput
          v-if="actionType === ExceptionActionType.REVERT"
          v-model="reversalJustification"
          placeholder="Digite a justificativa da reversão..."
          class="w-full mt-4"
          data-testid="reversal-justification-input"
        />
      </div>
    </div>

    <template #footer>
      <!-- Trava do Pós-Plantão (RN-07 / RF-12): cancelar ou reverter um pedido
           aprovado remove o override de valor e devolve o plantão à política —
           mudança direta no plantão já aprovado. -->
      <div
        v-if="!isConfirming && postStationLocked"
        class="flex items-start gap-2 px-3 py-2 mt-6 rounded-md border border-primary30 bg-primary5 text-sm text-textPrimary text-left"
        data-testid="summary-exception-post-station-lock-banner"
      >
        <SVGIcon :path="mdiLock" class="w-4 h-4 mt-0.5 shrink-0 text-primary30" />
        <span>{{ POST_STATION_LOCK_BANNER_TEXT }}</span>
      </div>

      <div v-if="!isConfirming" class="flex flex-row gap-4">
        <div
          v-if="actionType === ExceptionActionType.CANCEL"
          class="w-full flex flex-row gap-8 mt-6"
        >
          <FilledButton
            text="Voltar"
            width="46%"
            @click="closeSidebar"
            class="!bg-primary30"
            height="48px"
          />

          <OutlinedButton
            v-if="canCancelException"
            text="Cancelar pedido"
            width="46%"
            data-testid="cancel-exception-button"
            :disabled="postStationLocked"
            @click="startConfirmation"
            height="48px"
            class="flex-grow !border-negative !text-negative !bg-white"
          />
        </div>

        <div v-if="actionType === ExceptionActionType.REVERT" class="w-full mt-6">
          <!-- Com a reversão pendente não há o que iniciar: o voto é dos demais. -->
          <p
            v-if="hasPendingReversal"
            class="text-sm text-textPrimary text-left"
            data-testid="summary-reversal-pending-message"
          >
            Reversão pendente. Aguardando a validação dos demais responsáveis.
          </p>

          <template v-else-if="canRevertException">
            <!-- Motor ligado, mas sem papel da raiz: não há como votar na reversão. -->
            <div
              v-if="isWithoutReversalRole"
              class="bg-warning/20 border border-warning rounded-lg p-3 text-sm text-yellow-800 mb-4 text-left"
              data-testid="no-available-roles-message"
            >
              Você não tem papel de validação na primeira etapa do fluxo desta escala, por isso não
              pode solicitar a reversão. Peça ao administrador da escala.
            </div>
            <FilledButton
              :text="isMultiStepReversal ? 'Solicitar reversão' : 'Reverter aprovação'"
              width="100%"
              data-testid="revert-exception-button"
              :disabled="postStationLocked || isWithoutReversalRole"
              @click="startConfirmation"
              class="!bg-primary30"
              height="48px"
            />
          </template>
        </div>
      </div>

      <div v-else class="flex justify-around gap-8 mt-6">
        <FilledButton
          text="Cancelar"
          width="46%"
          @click="cancelConfirmation"
          height="48px"
          class="!bg-primary30"
        />

        <OutlinedButton
          text="Confirmar"
          width="46%"
          data-testid="confirm-action-button"
          :disabled="isReversalRoleMissing"
          @click="handleConfirmAction"
          :loading="actionLoading"
          height="48px"
          class="flex-grow !border-negative !text-negative !bg-white"
        />
      </div>
    </template>
  </BaseSidebar>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import BaseSidebar from '@/components/designSystem/Sidebar/BaseSidebar.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import ExceptionSimpleCard from '../Cards/ExceptionSimpleCard.vue'
import ExceptionWorkingDayCard from '../Cards/ExceptionWorkingDayCard.vue'
import StatusBadge from '../Badges/StatusBadge.vue'
import TextareaInput from '../Textarea/TextareaInput.vue'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import ExceptionService from '@/Network/Services/DoctorStationException'
import { formatDateToString, formatDateTimeWithTimezone } from '@/utils/date'
import { formatRepassMode, formatProportionalityRule } from '@/utils/formatters'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

import {
  ExceptionActionType,
  ExceptionStatusEnum,
  type ExceptionSummary
} from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import ExceptionAgreedValueCard from '../Cards/ExceptionAgreedValueCard.vue'
import StationContextCard from '../Cards/StationContextCard.vue'
import ExceptionalityProgressTimeline from '@/modules/company/Approvals/AuditoriumExceptionalities/components/ExceptionalityProgressTimeline.vue'
import { mapApprovalToTimeline, mapReversalToTimeline } from '@/utils/approval-timeline-mapper'
import { isReversalPending } from '@/utils/exception-reversal'
import { useApprovalRole } from '@/composables/useApprovalRole'
import ApprovalRoleSelector from '@/components/designSystem/ApprovalRoleSelector/index.vue'
import { VALIDATION_ROLE_LABELS } from '@/types/approval-flow.types'
import type { ReversalOutcome } from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import { mdiLock } from '@mdi/js'
import SVGIcon from '@/components/SVGIcon.vue'
import { POST_STATION_LOCK_BANNER_TEXT } from '@/modules/company/PostStation/constants'
import {
  getPostStationApprovedMessage,
  isPostStationApprovedError
} from '@/modules/company/PostStation/errors'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'

const toast = useToast()
const { notifyError } = useErrorToast()
const exceptionService = new ExceptionService()
const permissionsStore = usePermissionsStore()
const { flags } = useFeatureFlags()

interface StationContextData {
  doctorName: string
  doctorCrm?: string
  scheduleName: string
  positionName: string
  shiftName?: string
  team?: string
  timeRange: string
  dutyType?: string
  value?: string
  formation?: string
  repassMode?: string
  proportionalityRule?: string
  breadcrumb?: string
  allocatedBy?: string
  startDetail?: string
  endDetail?: string
  timezone?: string
}

const props = withDefaults(
  defineProps<{
    isOpen: boolean
    exceptionId: number | null
    scheduleId: number | null
    actionType: ExceptionActionType
    stationContext?: StationContextData
    /**
     * Trava do Pós-Plantão (RN-07 / RF-12) do plantão do pedido — ver
     * `RequestExceptionalitySidebar`. Componente de design system não sonda
     * `Network.postStation`: quem calcula é o módulo que abre a sidebar.
     */
    postStationLocked?: boolean
  }>(),
  { postStationLocked: false }
)

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
  (e: 'actionCompleted'): void
  (e: 'detailsLoaded', details: ExceptionSummary): void
}>()

const canCancelException = computed(() => {
  if (!props.scheduleId) return false
  return permissionsStore.check({
    permission: AuthorizationPermissions.StationExceptionalitiesCancelling,
    resource: {
      objectType: AuthorizationResourcesAndSubjects.Schedule,
      objectId: props.scheduleId
    }
  })
})

const canRevertException = computed(() => {
  if (!props.scheduleId) return false
  return permissionsStore.check({
    permission: AuthorizationPermissions.StationExceptionalitiesReverting,
    resource: {
      objectType: AuthorizationResourcesAndSubjects.Schedule,
      objectId: props.scheduleId
    }
  })
})

const isConfirming = ref(false)
const loading = ref(false)
const actionLoading = ref(false)
const reversalJustification = ref('')
const detailedException = ref<ExceptionSummary | null>(null)

/**
 * Reversão multi-etapa: o backend manda `reversalRolesForCurrentUser` como
 * lista (vazia ou não). Ausente/`null` = reversão imediata, como antes.
 */
const isMultiStepReversal = computed(
  () =>
    flags.enabledApprovalEngine &&
    Array.isArray(detailedException.value?.reversalRolesForCurrentUser)
)

const {
  availableRoles: availableReversalRoles,
  selectedRole,
  effectiveRole,
  isRoleMissing,
  resetRole
} = useApprovalRole(() => detailedException.value?.reversalRolesForCurrentUser)

/** Motor ligado, mas o usuário não tem papel da raiz do fluxo: não pode reverter. */
const isWithoutReversalRole = computed(
  () => isMultiStepReversal.value && availableReversalRoles.value.length === 0
)

const isReversalRoleMissing = computed(
  () =>
    props.actionType === ExceptionActionType.REVERT &&
    isMultiStepReversal.value &&
    isRoleMissing.value
)

const hasPendingReversal = computed(() =>
  isReversalPending(detailedException.value, flags.enabledApprovalEngine)
)

const reversalTimelineProps = computed(() => {
  const instance = detailedException.value?.reversalInstance
  return instance ? mapReversalToTimeline(instance) : null
})

// Toast de quem INICIA a reversão, por `outcome`. Backend antigo não manda
// `outcome`: cai na mensagem de reversão imediata, como antes.
const REVERSAL_REQUEST_TOASTS: Record<ReversalOutcome, string> = {
  PENDING: 'Reversão solicitada. Aguardando demais validadores.',
  REVERTED: 'Reversão realizada com sucesso!',
  REVERSAL_REJECTED: 'Reversão recusada. A excepcionalidade continua aprovada.'
}

const timelineProps = computed(() => {
  const instance = detailedException.value?.approvalInstance
  if (!instance) return null
  return mapApprovalToTimeline({
    approvalInstance: instance,
    requestDate: detailedException.value!.requestDate,
    requesterName: detailedException.value!.requester.name
  })
})

const approvedApproversText = computed(() => {
  const decisions = detailedException.value?.approvalInstance?.decisions
  if (!decisions) return ''
  return decisions
    .filter((d) => d.decision === 'APPROVED' && d.userName)
    .map((d) => d.userName)
    .join(', ')
})

const evaluationDateLabel = computed(() => {
  const status = detailedException.value?.status
  if (status === ExceptionStatusEnum.APPROVED) return 'Data da aprovação'
  if (status === ExceptionStatusEnum.REJECTED) return 'Data da reprovação'
  return 'Data da avaliação'
})

const evaluationDateDisplay = computed(() => {
  if (!detailedException.value) return null
  const date = detailedException.value.approvalDate ?? detailedException.value.evaluationDate
  return date ? formatDateToString(new Date(date)) : null
})

const evaluatorOrApproverName = computed(() => {
  return (
    detailedException.value?.evaluator?.name || detailedException.value?.approver?.name || 'N/A'
  )
})

const modelValue = computed({
  get: () => props.isOpen,
  set: (value) => {
    if (!value) {
      isConfirming.value = false
      reversalJustification.value = ''
      resetRole()
      detailedException.value = null
      loading.value = false
      actionLoading.value = false
    }
    emit('update:isOpen', value)
  }
})

const headerId = computed(() => {
  return `<span class="inline-block bg-white py-2 px-1 rounded-lg text-2xl">#${props.exceptionId?.toString().padStart(5, '0') || 'N/A'}</span>`
})

watch(
  [() => props.isOpen, () => props.exceptionId],
  async ([newIsOpen, newExceptionId]) => {
    if (!newIsOpen || newExceptionId === null) {
      detailedException.value = null
      return
    }

    if (detailedException.value?.id === newExceptionId) {
      return
    }

    loading.value = true
    try {
      const details = await exceptionService.getDetails(newExceptionId)
      detailedException.value = details
      emit('detailsLoaded', details)
    } catch (error) {
      console.error('Falha ao buscar detalhes da excepcionalidade:', error)
      detailedException.value = null
      notifyError(error, 'Não foi possível carregar os detalhes do pedido')
    } finally {
      loading.value = false
    }
  },
  { immediate: true }
)

const confirmationTitle = computed(() => {
  if (props.actionType === ExceptionActionType.CANCEL) {
    return 'Tem certeza que deseja cancelar esse pedido?'
  } else if (props.actionType === ExceptionActionType.REVERT) {
    return isMultiStepReversal.value
      ? 'Tem certeza que deseja solicitar a reversão dessa aprovação?'
      : 'Tem certeza que deseja reverter essa aprovação?'
  }
  return 'Confirmação'
})

const confirmationMessage = computed(() => {
  const id = detailedException.value?.id || props.exceptionId
  // Multi-etapa: o pedido só dispara o fluxo; a reversão ainda pode ser recusada.
  if (props.actionType === ExceptionActionType.REVERT && isMultiStepReversal.value) {
    return `Deseja solicitar a reversão da aprovação do pedido #${id}? A reversão só será aplicada após a validação dos demais responsáveis.`
  }
  const actionText =
    props.actionType === ExceptionActionType.CANCEL
      ? 'cancelar o pedido'
      : 'reverter a aprovação do pedido'
  return `Deseja ${actionText} #${id}? Essa ação não poderá ser desfeita.`
})

function startConfirmation() {
  isConfirming.value = true
}

function cancelConfirmation() {
  isConfirming.value = false
  reversalJustification.value = ''
}

function closeSidebar() {
  modelValue.value = false
}

// Verbo das mensagens de erro/log da ação em curso.
const actionVerb = computed(() =>
  props.actionType === ExceptionActionType.CANCEL ? 'cancelar' : 'reverter'
)

// Executa o endpoint da ação confirmada. `VIEW` não tem ação: cai fora sem
// chamar nada (mesma semântica do `if/else if` original).
async function executeAction(exceptionId: number) {
  if (props.actionType === ExceptionActionType.CANCEL) {
    await exceptionService.cancel(exceptionId, Number(props.scheduleId))
    toast.success('Solicitação cancelada com sucesso!')
  } else if (props.actionType === ExceptionActionType.REVERT) {
    const result = await exceptionService.revert(
      exceptionId,
      reversalJustification.value,
      props.scheduleId || 0,
      isMultiStepReversal.value ? effectiveRole.value : undefined
    )
    toast.success(
      (result?.outcome && REVERSAL_REQUEST_TOASTS[result.outcome]) ||
        'Reversão realizada com sucesso!'
    )
  }
}

async function handleConfirmAction() {
  if (!detailedException.value) {
    toast.error('Detalhes do pedido não disponíveis.')
    return
  }

  if (props.actionType === ExceptionActionType.REVERT && !reversalJustification.value.trim()) {
    toast.error('Por favor, informe a justificativa da reversão.')
    return
  }

  actionLoading.value = true
  try {
    await executeAction(detailedException.value.id)
    emit('actionCompleted')
    // Só fecha na trilha de sucesso: em erro a sidebar fica aberta para o
    // usuário ler a mensagem e tentar de novo (o setter de `modelValue` faz a
    // limpeza de estado).
    modelValue.value = false
  } catch (error) {
    console.error(`Erro ao ${actionVerb.value} a solicitação:`, error)

    // Sem este ramo o erro é SOBRESCRITO por "Não foi possível cancelar/reverter
    // a solicitação. Tente novamente." — que manda o usuário repetir uma ação
    // que vai falhar de novo, em vez de dizer para reverter a validação.
    if (isPostStationApprovedError(error)) {
      toast.error(getPostStationApprovedMessage(error))
      return
    }

    // Antes: fallback estático que mandava "tentar novamente" mesmo quando o
    // backend explicava o motivo — a mensagem do ServiceError tem prioridade.
    notifyError(error, `Não foi possível ${actionVerb.value} a solicitação`)
  } finally {
    actionLoading.value = false
  }
}
</script>
